import type { StateCreator } from "zustand";
import { v4 as uuidv4 } from "uuid";

import type { TrailSegment } from "../types/chatTrail";
import { isWholeMathRun, splitMathSegments } from "../utils/mathRun";
import { createStore } from "./createStore";

export const THINKING_STATES = [
  "Thinking",
  "Doing math",
  "Analyzing",
  "Taking a look",
] as const;

export const CHAT_RESPONSE_TIMEOUT_MS = 20000;

export const REPLYING_STATUS = "Replying";

export const CHAT_TIMEOUT_COPY =
  "The tutor didn't reply. Send your message again to retry.";

export type ChatRole = "student" | "tutor";

export type { TrailSegment };

export const segmentsToText = (segments: TrailSegment[]): string =>
  segments.map((segment) => segment.text).join("");

export interface ChatTrailMessage {
  id: string;
  role: ChatRole;
  segments: TrailSegment[];
  pending: boolean;
  status?: string;
  error?: string;
}

export interface TrailScope {
  session: string | number;
  bite: number;
}

export interface ChatTrailState {
  messages: ChatTrailMessage[];
  turnCount: number;
  pendingIndex: number;
  scope: TrailScope | null;
  epoch: number;

  enterScope: (scope: TrailScope) => void;

  appendStudentMessage: (text: string) => void;
  beginTutorTurn: () => void;
  appendTutorText: (text: string, flaggedMath?: boolean) => void;
  sealTutorTurn: () => void;
  failTutorTurn: (message: string) => void;
  clearTrail: () => void;
}

const INITIAL = {
  messages: [] as ChatTrailMessage[],
  turnCount: 0,
  pendingIndex: -1,
  scope: null as TrailScope | null,
  epoch: 0,
};

let responseTimer: ReturnType<typeof setTimeout> | null = null;

const clearResponseTimer = () => {
  if (responseTimer) clearTimeout(responseTimer);
  responseTimer = null;
};

const sameScope = (a: TrailScope | null, b: TrailScope): boolean =>
  a !== null && a.session === b.session && a.bite === b.bite;

const base: StateCreator<ChatTrailState> = (set, get) => ({
  ...INITIAL,

  enterScope: (next) => {
    const current = get().scope;
    if (sameScope(current, next)) return;

    if (current === null) {
      set({ scope: next });
      return;
    }

    clearResponseTimer();
    set({ ...INITIAL, scope: next, epoch: get().epoch + 1 });
  },

  appendStudentMessage: (text) => {
    set((state) => ({
      messages: [
        ...state.messages,
        {
          id: uuidv4(),
          role: "student",
          segments: splitMathSegments(text),
          pending: false,
        },
      ],
    }));
  },

  beginTutorTurn: () => {
    clearResponseTimer();
    responseTimer = setTimeout(() => {
      get().failTutorTurn(CHAT_TIMEOUT_COPY);
    }, CHAT_RESPONSE_TIMEOUT_MS);

    set((state) => ({
      turnCount: state.turnCount + 1,
      pendingIndex: state.messages.length,
      messages: [
        ...state.messages,
        {
          id: uuidv4(),
          role: "tutor",
          segments: [],
          pending: true,
          status: THINKING_STATES[state.turnCount % THINKING_STATES.length],
        },
      ],
    }));
  },

  appendTutorText: (text, flaggedMath = false) => {
    const { pendingIndex, messages } = get();
    if (!text || pendingIndex === -1) return;

    clearResponseTimer();
    const isMath = flaggedMath || isWholeMathRun(text);
    const next = messages.slice();
    const segments = next[pendingIndex].segments.slice();
    const last = segments[segments.length - 1];

    const separator = !last || /^[.,!?;:]/.test(text) ? "" : " ";

    if (isMath || !last || last.isMath) {
      segments.push({ text: separator + text, isMath });
    } else {
      segments[segments.length - 1] = {
        text: last.text + separator + text,
        isMath: false,
      };
    }

    next[pendingIndex] = {
      ...next[pendingIndex],
      segments,
      status: REPLYING_STATUS,
    };
    set({ messages: next });
  },

  sealTutorTurn: () => {
    const { pendingIndex, messages } = get();
    if (pendingIndex === -1) return;

    clearResponseTimer();
    const sealed = messages[pendingIndex];

    set({
      pendingIndex: -1,
      messages: sealed.segments.length
        ? [
            ...messages.slice(0, pendingIndex),
            { ...sealed, pending: false, status: undefined },
            ...messages.slice(pendingIndex + 1),
          ]
        : messages.filter((_, index) => index !== pendingIndex),
    });
  },

  failTutorTurn: (message) => {
    const { pendingIndex, messages } = get();
    if (pendingIndex === -1) return;

    clearResponseTimer();
    const next = messages.slice();
    next[pendingIndex] = {
      ...next[pendingIndex],
      pending: false,
      status: undefined,
      error: message,
    };
    set({ messages: next, pendingIndex: -1 });
  },

  clearTrail: () => {
    clearResponseTimer();
    set({ ...INITIAL, epoch: get().epoch + 1 });
  },
});

export const useChatTrailStore = createStore<ChatTrailState>(
  "ChatTrailStore",
  base,
);
